import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import ReactSelect from 'react-select';
import Creatable from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import makeAnimated from 'react-select/animated';

import If from 'utils/If';
import { getStyles, formatGroupLabel } from './utils';

export default function Select({
  label,
  error,
  isMulti = false,
  isAsync = false,
  isCreatable = false,
  selectAllLabel = 'Select All',
  options,
  value,
  onChange = () => {},
  wrapperStyle = {},
  wrapperId = '',
  inputId = '',
  inputWrapperId = '',
  wrapperClassName = '',
  labelStyle = {},
  labelClassName = '',
  inputWrapperClassName = '',
  inputClassName = '',
  animate = null,
  overridingStyles = null,
  ...reactSelectProps
}) {
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [animatedComponents, setAnimatedComponents] = useState(null);
  const [numberOfOptions, setNumberOfOptions] = useState(-1);
  const [allOptions, setAllOptions] = useState(options);
  let HybridSelect = ReactSelect;
  if (isCreatable) {
    HybridSelect = Creatable;
  } else if (isAsync) {
    HybridSelect = AsyncSelect;
  }

  useEffect(() => {
    // set if animation to be attached to Select
    animate && setAnimatedComponents(makeAnimated());
  }, []);

  useEffect(() => {
    // set all options option value and total number of options available
    if (options instanceof Array) {
      const allOptions = options.reduce((acc, option) => {
        if (option.value) return [...acc, option];
        else if (option.options) return [...acc, ...option.options];
        return acc;
      }, []);
      setAllOptions(allOptions);
      setNumberOfOptions(allOptions.length);
    } else {
      setNumberOfOptions(0);
    }
  }, [options]);

  const changeHandler = (option) => {
    let newOption = option;
    if (isMulti) {
      const optionsList = option ? [...option] : [];
      newOption = optionsList;
      const newlyAddedOption = optionsList[optionsList.length - 1];
      if (newlyAddedOption && newlyAddedOption.id === 'all-options') {
        setAreAllSelected(true);
        return onChange(newlyAddedOption.value); // all options
      } else {
        setAreAllSelected(false);
      }
      if (optionsList.length === numberOfOptions) {
        setAreAllSelected(true);
      }
    }
    onChange(newOption);
  };

  let selectOptions = options || [];
  if (isMulti && !areAllSelected) {
    const optionsList = options ? options : [];
    selectOptions = [
      { label: selectAllLabel, value: allOptions, id: 'all-options' },
      ...optionsList,
    ];
  }
  const isGrouped = numberOfOptions !== selectOptions.length;
  const selectStyle = overridingStyles || getStyles({ error });

  return (
    <div
      className={cn('uiKit__element', wrapperClassName)}
      style={wrapperStyle}
      id={wrapperId}
    >
      <If test={label}>
        <div className={cn('uiKit__element--labelWrapper', labelClassName)}>
          <label style={labelStyle}>{label}</label>
        </div>
      </If>
      <div
        className={cn('uiKit__select', inputWrapperClassName)}
        id={inputWrapperId}
      >
        <HybridSelect
          id={inputId}
          value={value}
          isMulti={isMulti}
          styles={selectStyle}
          options={selectOptions}
          formatGroupLabel={formatGroupLabel(isGrouped)}
          components={animatedComponents}
          onChange={changeHandler}
          className={cn('uiKit__select__box', inputClassName)}
          classNamePrefix="uiKit__select__box"
          {...reactSelectProps}
        />
        <If test={error}>
          <p className="uiKit__select--error">{error}</p>
        </If>
      </div>
    </div>
  );
}
